import React, { useMemo, useCallback, useState } from 'react'
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { StatementColumn } from '../../types'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { EnhancedStatementRow } from '../../utils/dataEnhancement'
import { filtersSelector } from '../../../../redux/context/filters/selectors'
import { createExcelStyles } from '../../../../shared/utils/excelStyles'
import { useFinancialStatementsState } from '../../hooks/useFinancialStatementsState'
import { useBalanceSheetColumns } from '../../hooks/useBalanceSheetColumns'
// eslint-disable-next-line import/no-internal-modules
import { useVoucherModal } from '../../../vouchers/components/VoucherModal/useVoucherModal'
import { useRowFiltering } from '../../hooks/useRowFiltering'
import { useAsyncGridData } from '../../hooks/useAsyncGridData'
import { useTreeDataTransaction } from '../../hooks/useTreeDataTransaction'
import { useCtxReportablePeriodGroups } from '../../../fiscalYear/hooks/useCtxReportablePeriodGroups'
import { getCellSelectionConfig, getStatusBarConfig, getColumnsSideBarConfig, getRowClass } from './utils/gridConfig'
import { useGridContext } from '../../../../shared/hooks/useGridContext'
import { GridToolbar } from '../../../../shared/components/GridToolbar'
import { NumberFormatControls } from '../../../../shared/components/NumberFormatControls'
import { DisplayScale, NumberFormatConfig } from '../../../../shared/utils/numberScale'
import './styles.css'
import { useExcelExport } from '../../hooks/useExcelExport'

interface BalanceSheetTableProps {
  treeData: EnhancedStatementRow[]
  columns: StatementColumn[]
  error?: any
  loading?: boolean
  statementType: 'assets' | 'liabilities'
  companyId: string
  financialStatementsFilters: ReturnType<typeof useFinancialStatementsState>['financialStatementsFilters']
  hideControls?: boolean
  displayScale?: DisplayScale
  onDisplayScaleChange?: (scale: DisplayScale) => void
  decimals?: number
  onDecimalsChange?: (decimals: number) => void
}

const noop = () => undefined

const BalanceSheetTable: React.FC<BalanceSheetTableProps> = ({
  treeData,
  columns,
  error,
  loading = false,
  statementType,
  companyId,
  financialStatementsFilters,
  hideControls = false,
  displayScale = DisplayScale.default,
  onDisplayScaleChange = noop,
  decimals = 0,
  onDecimalsChange = noop
}) => {
  const { t } = useTranslation()
  const [gridApi, setGridApi] = useState<GridApi<EnhancedStatementRow>>()
  // No scale suffix (k/M) in cells — the current scale is already shown by the toolbar toggle above the table.
  const numberFormat = useMemo<NumberFormatConfig>(
    () => ({ decimals, displayScale, showScaleSuffix: false }),
    [decimals, displayScale]
  )
  const gridContext = useGridContext({ gridApi, numberFormat })
  const { displayEmptyRows, dataTypes } = useSelector(filtersSelector)
  // Note: companyId and financialStatementsFilters are now passed as props from parent
  // to ensure consistent query keys across tabs
  const [voucherModal, openVoucherModal] = useVoucherModal()

  // Voucher modal handler
  const handleShowVouchers = useCallback(
    (row: EnhancedStatementRow, column: StatementColumn) => {
      openVoucherModal({ row, column })
    },
    [openVoucherModal]
  )

  // Get fiscal years/period groups
  const periodGroups = useCtxReportablePeriodGroups()

  // Custom hooks
  const { isExternalFilterPresent, doesExternalFilterPass } = useRowFiltering(displayEmptyRows, columns, periodGroups)

  // Update grid data using transactions to preserve expanded state and async-loaded rows
  useTreeDataTransaction(
    gridApi,
    treeData,
    loading,
    companyId,
    financialStatementsFilters.budgetingScenarioId,
    financialStatementsFilters.dimensions
  )

  // Generic async data handler - handles investment, amendment, and dimension loading automatically
  const onRowGroupOpened = useAsyncGridData(
    gridApi,
    companyId,
    financialStatementsFilters.budgetingScenarioId,
    financialStatementsFilters.periodGroups
  )

  // Column definitions - no budgeting features
  const { columnDefs, autoGroupColumnDef, getContextMenuItems } = useBalanceSheetColumns({
    columns,
    onShowVouchers: handleShowVouchers,
    // onRowAction: handleRowAction,
    dataTypes
  })

  // Grid configuration
  const onExcelExport = useExcelExport(gridApi, statementType, gridContext)
  const excelStyles = useMemo(() => createExcelStyles(), [])
  const cellSelection = useMemo(() => getCellSelectionConfig(), [])
  const statusBar = useMemo(() => getStatusBarConfig(), [])
  const sideBar = useMemo(() => getColumnsSideBarConfig(), [])

  // Grid options - fixed row height (no dual value rendering)
  const gridOptions = useMemo(
    (): GridOptions<EnhancedStatementRow> => ({
      autoGroupColumnDef,
      columnDefs,
      getContextMenuItems,
      defaultColDef: {
        resizable: true,
        rowDrag: false,
        sortable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        autoHeaderHeight: true,
        wrapHeaderText: true,
        enableCellChangeFlash: true
      },
      hidePaddedHeaderRows: true,
      cellSelection: true,
      // rowHeight: 24,
      context: {
        ...gridContext,
        statementType
      },
      getRowClass,
      treeData: true,
      excludeChildrenWhenTreeDataFiltering: true,
      getDataPath: row => row.path,
      getRowId: params => params.data.key
    }),
    [columnDefs, autoGroupColumnDef, getContextMenuItems, statementType, gridContext]
  )

  // Event handlers
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api)
  }, [])

  // Error handling
  if (error) {
    return <div>Error: {error.message}</div>
  }

  return (
    <>
      {!hideControls && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {t(`financialStatementsPage:${statementType}`)}
          </h2>
        </div>
      )}
      {!hideControls && (
        <GridToolbar>
          <GridToolbar.Actions>
            <NumberFormatControls
              displayScale={displayScale}
              onDisplayScaleChange={onDisplayScaleChange}
              decimals={decimals}
              onDecimalsChange={onDecimalsChange}
            />
            <Button onClick={onExcelExport} icon={<DownloadOutlined />}>
              {t('financialStatementsPage:excel')}
            </Button>
          </GridToolbar.Actions>
        </GridToolbar>
      )}

      <div style={{ flex: 1 }}>
        <CustomAgGrid
          {...gridOptions}
          rowData={undefined} // Data managed via transactions in useTreeDataTransaction
          loading={loading}
          excelStyles={excelStyles}
          statusBar={statusBar}
          cellSelection={cellSelection}
          sideBar={sideBar}
          onGridReady={onGridReady}
          onRowGroupOpened={onRowGroupOpened}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </div>

      {voucherModal}
    </>
  )
}

export default BalanceSheetTable
